import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, can, getUser } from '../lib/api';
import { Card, Empty, ErrorNote, Field, Loading, Modal, PageHead, Pill, useResource, useToast } from '../components/ui';
import { dayTime, number } from '../lib/format';

/**
 * Today: the DPO office's work list. One table, overdue first, then by due
 * time. Each row says what, for whom (masked), by when and the one next step.
 */

type Kind = 'withdrawal' | 'request' | 'erasure_notice' | 'erasure_permitted' | 'processor_confirmation' | 'delivery_failure';

interface Row {
  kind: Kind;
  what: string;
  who: string | null;
  due: string | null;
  overdue: boolean;
  next: { label: string; href: string };
  ref?: { eventId?: string; requestId?: string; taskId?: string };
}

interface TodayList {
  asOf: string;
  rows: Row[];
  counts: Record<Kind, number> & { overdue: number };
  withdrawals: {
    open: number; breached: number;
    fiduciary: { onTime: number; late: number };
    processors: { onTime: number; late: number; breached: number };
  };
}

const KIND_LABEL: Record<Kind, string> = {
  withdrawal: 'Withdrawals',
  request: 'Requests',
  erasure_notice: 'Erasure notices',
  erasure_permitted: 'Erasures',
  processor_confirmation: 'Processor confirmations',
  delivery_failure: 'Delivery failures',
};

function status(r: Row): { label: string; tone?: 'bad' | 'warn' } {
  if (r.overdue) return { label: 'Overdue', tone: 'bad' };
  if (!r.due) return { label: r.kind === 'delivery_failure' ? 'Failing' : 'Open', tone: r.kind === 'delivery_failure' ? 'bad' : undefined };
  if (r.kind === 'erasure_permitted') return { label: 'Permitted' };
  const hours = (new Date(r.due).getTime() - Date.now()) / 3_600_000;
  if (hours <= 24) return { label: 'Due within a day', tone: 'warn' };
  return { label: `Due in ${Math.ceil(hours / 24)} days` };
}

export default function Today() {
  const user = getUser();
  const [kind, setKind] = useState<Kind | ''>('');
  const [reload, setReload] = useState(0);
  const [stopping, setStopping] = useState<Row | null>(null);
  const data = useResource<TodayList>(() => api.get('/v1/today'), [reload]);
  const rows = (data.data?.rows ?? []).filter((r) => !kind || r.kind === kind);
  const w = data.data?.withdrawals;

  return (
    <>
      <PageHead title="Today" description="Work due for the DPO office: withdrawals to stop, requests near their date, erasure notices, processor confirmations and delivery failures." />
      <ErrorNote error={data.error} />

      {w && (
        <div className="small muted" style={{ marginBottom: 10 }}>
          Withdrawals, last 90 days: stop recorded on time {number(w.fiduciary.onTime)}, late {number(w.fiduciary.late)}
          {w.breached ? <>, <span className="bad-text">{number(w.breached)} past the stop time with no stop recorded</span></> : null}.
          {' '}Processors acknowledged on time {number(w.processors.onTime)}, late {number(w.processors.late)}
          {w.processors.breached ? <>, <span className="bad-text">{number(w.processors.breached)} not acknowledged past the stop time</span></> : null}.
        </div>
      )}

      <div className="tabs">
        <button className={kind === '' ? 'active' : ''} onClick={() => setKind('')}>
          All{data.data ? <span className="tab-count">{data.data.rows.length}</span> : null}
        </button>
        {(Object.keys(KIND_LABEL) as Kind[]).filter((k) => data.data?.counts[k]).map((k) => (
          <button key={k} className={kind === k ? 'active' : ''} onClick={() => setKind(k)}>
            {KIND_LABEL[k]}<span className="tab-count">{data.data!.counts[k]}</span>
          </button>
        ))}
      </div>

      <Card>
        {data.loading && !data.data && <Loading what="Loading today's list" />}
        {data.data && rows.length === 0 && <Empty>Nothing due.</Empty>}
        {rows.length > 0 && (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr><th>What</th><th>Who</th><th>Due</th><th>Status</th><th>Next step</th></tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const s = status(r);
                  return (
                    <tr key={`${r.kind}-${i}`}>
                      <td>{r.what}</td>
                      <td className="small">{r.who ?? <span className="muted">—</span>}</td>
                      <td className="small">{r.due ? dayTime(r.due) : '—'}</td>
                      <td><Pill tone={s.tone}>{s.label}</Pill></td>
                      <td>
                        {r.kind === 'withdrawal' && r.ref?.eventId && can(user, 'requests:handle')
                          ? <button className="small" onClick={() => setStopping(r)}>{r.next.label}</button>
                          : <Link to={r.next.href}>{r.next.label}</Link>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {stopping && (
        <StopModal row={stopping} onClose={() => setStopping(null)} onDone={() => { setStopping(null); setReload((n) => n + 1); }} />
      )}
    </>
  );
}

/** Today's local date and time in the form a datetime-local input takes. */
function localNow(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function StopModal({ row, onClose, onDone }: { row: Row; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [at, setAt] = useState(localNow());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      const r = await api.post<{ withdrawal: { fiduciary: { status: string } } }>(
        `/v1/withdrawals/${row.ref!.eventId}/stop`, { stoppedAt: new Date(at).toISOString(), note },
      );
      toast(r.withdrawal.fiduciary.status === 'late' ? 'Stop recorded, after the stop time.' : 'Stop recorded, on time.', 'ok');
      onDone();
    } catch (err) {
      toast((err as Error).message, 'bad');
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Record processing stopped"
      onClose={onClose}
      footer={<><button onClick={onClose} disabled={busy}>Cancel</button><button className="primary" onClick={save} disabled={busy || !at}>{busy ? 'Saving' : 'Record stop'}</button></>}
    >
      <dl className="kv">
        <dt>Withdrawal</dt><dd>{row.what.replace(/^Stop processing: /, '')}</dd>
        <dt>Person</dt><dd>{row.who ?? '—'}</dd>
        <dt>Stop due by</dt><dd>{row.due ? dayTime(row.due) : '—'}</dd>
      </dl>
      <Field label="Stopped at" help="When the organisation's own systems stopped processing for this purpose.">
        <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
      </Field>
      <Field label="Note" help="Optional. For example, the system and who made the change.">
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Modal>
  );
}
